import WatchlistTabs, { activeDataSource } from './WatchlistTabs';
import {
    generatePath,
    useHistory,
    useParams,
    useRouteMatch,
} from 'react-router-dom';
import { reduceIdsByType, reduceIdsWithSelectedFields } from './utils';
import { useCountryParam, useCurrentCountry } from 'utils/hooks';
import { useEffect, useMemo, useState } from 'react';
import AddBySearch from 'components/Navigation/Search/AddBySearch';
import Drawer from 'components/Drawer';
import DrawerContent from 'components/DrawerContent';
import InstagramWatchlist from './Instagram/InstagramWatchlist';
import { Loading } from 'components';
import OverviewWatchlist from './Overview/OverviewWatchlist';
import PageBar from 'components/PageBar';
import { PageHeading } from '../CategoryTrends';
import { PagePadding } from 'components/PagePadding';
import { PlaceholderText } from 'components/Placeholders';
import { SUPPORTED_TYPES_BY_DATASOURCE } from './constants';
import SearchWatchlist from './Google/SearchWatchlist';
import { SectionCard } from 'components/SectionCard';
import Stack from 'components/Stack';
import TiktokWatchlist from './TikTok/TiktokWatchlist';
import WatchlistDescription from './WatchlistDescription';
import WatchlistMenu from './WatchlistMenu';
import { getWatchlistRenderHits } from './Buttons';
import { mixpanelTrack } from 'utils/app';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useGetWatchlistHashtagIds } from './useGetWatchlistHashtagIds';
import { useGetWatchlistRelatedCategoryPairs } from './useGetWatchlistRelatedCategoryPairs';
import { views } from '@/copy';

const headingSpace = (theme) => `${theme.space_3}px`;
const WatchlistHeading = styled(PageHeading)`
    font-size: ${({ theme }) => theme.fontSize_md};
    font-weight: ${({ theme }) => theme.fontExtrabold};
    margin: 0;
`;

const EmptyWatchlistParagraph = styled.p`
    text-align: left;
    max-width: 600px;
`;

function WatchlistNotFound() {
    return (
        <EmptyWatchlistParagraph>
            No watchlist could be found.
        </EmptyWatchlistParagraph>
    );
}

const { gettingStarted } = views.watchlist;

function EmptyWatchlist({
    hasUnsupportedItemsOnly,
    countryTextId,
    renderHits,
}) {
    const unsupportedItemsText =
        'Your watchlist has items, but none are supported in this datasource.';
    return (
        <Stack $direction="column" $space="1rem">
            {renderHits ? (
                <div css="display: grid; grid-template-columns: 600px; gap: 1rem;">
                    <div>
                        {hasUnsupportedItemsOnly
                            ? unsupportedItemsText
                            : gettingStarted}
                    </div>
                    <div>
                        <AddBySearch
                            countryTextId={countryTextId}
                            renderHits={renderHits}
                        />
                    </div>
                </div>
            ) : (
                <span>
                    {hasUnsupportedItemsOnly
                        ? unsupportedItemsText
                        : 'This watchlist is empty.'}
                </span>
            )}
        </Stack>
    );
}

export function WatchlistView({
    watchlist,
    watchlistId,
    watchlistItems,
    isLoadingWatchlists,
    isLoadingWatchlistItems,
    onAddItem,
    onRemoveItem,
    menuItems,
}) {
    const history = useHistory();
    const match = useRouteMatch();
    const params = useParams();
    const { dataSource } = params;
    const { mutateToWithCountryParam } = useCountryParam();
    const categoryIds = useMemo(
        () => reduceIdsByType(watchlistItems, 'category'),
        [watchlistItems],
    );
    const brandIds = useMemo(
        () => reduceIdsByType(watchlistItems, 'brand'),
        [watchlistItems],
    );
    const { hashtagCategoryIds, hashtagBrandIds } =
        useGetWatchlistHashtagIds(watchlistItems);
    const {
        relatedCategoryParentCategoryItems,
        relatedCategoryParentBrandItems,
        categoryPairs: tiktokRelatedCategoryPairs,
        brandPairs: tiktokRelatedBrandPairs,
    } = useGetWatchlistRelatedCategoryPairs(watchlistItems);
    const relatedCategoryCategoryIds = useMemo(
        () =>
            reduceIdsWithSelectedFields(
                watchlistItems,
                'related-category',
                ({ type }) => type === 'related-category',
                ['categoryId', 'relatedCategoryId'],
            ),
        [watchlistItems],
    );
    const relatedCategoryBrandIds = useMemo(
        () =>
            reduceIdsWithSelectedFields(
                watchlistItems,
                'related-category',
                ({ type }) => type === 'related-category',
                ['brandId', 'relatedCategoryId'],
            ),
        [watchlistItems],
    );
    const affinityCategoryIds = useMemo(
        () =>
            reduceIdsWithSelectedFields(
                watchlistItems,
                'affinity',
                ({ type }) => type === 'affinity',
                ['affinityId', 'categoryId'],
            ),
        [watchlistItems],
    );
    const affinityBrandIds = useMemo(
        () =>
            reduceIdsWithSelectedFields(
                watchlistItems,
                'affinity',
                ({ type }) => type === 'affinity',
                ['affinityId', 'brandId'],
            ),
        [watchlistItems],
    );
    const instagramHashtags = useMemo(
        () =>
            reduceIdsWithSelectedFields(
                watchlistItems,
                'instagram-hashtag',
                ({ type }) => type === 'instagram-hashtag',
                ['name'],
            ),
        [watchlistItems],
    );
    const country = useCurrentCountry();
    const countryId = country ? country.id : null;
    const countryTextId = country ? country.code : null;
    const [details, setDetails] = useState(null);
    const onRowClick = (e, { type, id, categoryId, brandId, affinityId }) =>
        setDetails({
            paginateBy: `${type}Id`,
            [`${type}Id`]: type === 'affinity' ? affinityId : id,
            ...(type === 'affinity'
                ? {
                      [categoryId ? 'categoryId' : 'brandId']:
                          categoryId || brandId,
                  }
                : {}),
            countryId: country ? country.id : null,
        });

    const watchlistName = watchlist && watchlist.name ? watchlist.name : null;

    const countryName = country && country.name ? country.name : null;

    useEffect(() => {
        if (watchlistName && countryName)
            mixpanelTrack('Watchlist', { name: watchlistName, countryName });
    }, [watchlistName, countryName]);

    useEffect(() => {
        const REDIRECT_FLAG_KEY = `watchlist-${watchlistId}-hasRedirected`;
        const hasRedirectedToTiktok = sessionStorage.getItem(REDIRECT_FLAG_KEY);
        if (
            hasRedirectedToTiktok === 'true' ||
            !watchlist ||
            !watchlistItems?.length
        )
            return;

        const supportedTypesForCurrentSource =
            SUPPORTED_TYPES_BY_DATASOURCE[dataSource || 'overview'] ??
            new Set();
        const currentTabHasRelevantItems = watchlistItems.some((item) =>
            supportedTypesForCurrentSource.has(item.type),
        );
        if (currentTabHasRelevantItems) return;

        // Try to find a fallback data source that *does* support at least one item
        const fallbackDataSource = Object.entries(
            SUPPORTED_TYPES_BY_DATASOURCE,
        ).find(
            ([source, supportedTypes]) =>
                source !== dataSource &&
                watchlistItems.some((item) => supportedTypes.has(item.type)),
        )?.[0];
        if (fallbackDataSource) {
            sessionStorage.setItem(REDIRECT_FLAG_KEY, 'true');
            const fallbackPath =
                fallbackDataSource === 'overview'
                    ? `${match.path}`
                    : `${match.path}/${fallbackDataSource}`;
            history.push(
                mutateToWithCountryParam(
                    generatePath(fallbackPath, { watchlistId }),
                ),
            );
        }
    }, [
        watchlist,
        watchlistItems,
        categoryIds,
        brandIds,
        hashtagCategoryIds,
        hashtagBrandIds,
    ]);

    const mixpanelMeta = {
        contentKey: 'Watchlist',
        country: country ? country.name : null,
    };

    const handleOnAddItem = (node) => {
        const supportsHashtags =
            SUPPORTED_TYPES_BY_DATASOURCE[dataSource]?.has('hashtag');
        if (node.type === 'hashtag' && !supportsHashtags) {
            toast.success(
                `${node.name} added. Switching to TikTok tab to view it.`,
                { position: 'top-right', duration: 4000 },
            );
            history.push(
                mutateToWithCountryParam(`/watchlist/${watchlistId}/tiktok`),
            );
        }
        onAddItem(node);
    };
    const renderHits = useMemo(
        () =>
            getWatchlistRenderHits({
                watchlistItems,
                onAddItem: handleOnAddItem,
                onRemoveItem,
                dataSource: 'google',
            }),
        [watchlistItems, onAddItem, onRemoveItem],
    );

    const supportedTypes =
        SUPPORTED_TYPES_BY_DATASOURCE[dataSource || 'overview'];
    const filteredWatchlistItems =
        watchlistItems?.filter((item) => supportedTypes?.has(item.type)) ?? [];
    const hasUnsupportedItemsOnly =
        watchlistItems?.length > 0 && filteredWatchlistItems.length === 0;
    return (
        <>
            <PagePadding>
                <PageBar />
                <Stack $space={(theme) => `${theme.space_7}px`}>
                    <Stack $space={headingSpace}>
                        <div css="display: flex; align-items: center;">
                            <WatchlistHeading>
                                {isLoadingWatchlists ? (
                                    <PlaceholderText width="10rem" />
                                ) : (
                                    (watchlist || {}).name || '-'
                                )}
                            </WatchlistHeading>
                            <Stack
                                $direction="row"
                                $space={(theme) => `${theme.space_5}px`}
                                css="margin-left: auto;"
                            >
                                {!(
                                    isLoadingWatchlistItems ||
                                    isLoadingWatchlists
                                ) &&
                                watchlist &&
                                filteredWatchlistItems.length ? (
                                    <>
                                        <WatchlistMenu
                                            menuItems={menuItems}
                                            watchlistId={watchlistId}
                                            watchlist={watchlist}
                                            watchlistItems={watchlistItems}
                                            mixpanelMeta={mixpanelMeta}
                                        />
                                    </>
                                ) : null}
                            </Stack>
                        </div>
                        {watchlist && watchlist.description ? (
                            <WatchlistDescription
                                isLoading={isLoadingWatchlists}
                                description={watchlist.description}
                                title={
                                    watchlist
                                        ? watchlist.name
                                        : 'Watchlist Description'
                                }
                            />
                        ) : isLoadingWatchlists ? (
                            <PlaceholderText width="15rem" />
                        ) : null}
                    </Stack>
                    <WatchlistTabs />
                    {!dataSource && (
                        <SectionCard css="padding-top: 20px;">
                            {isLoadingWatchlistItems || isLoadingWatchlists ? (
                                <Loading />
                            ) : watchlist && filteredWatchlistItems.length ? (
                                <OverviewWatchlist
                                    categoryIds={categoryIds}
                                    brandIds={brandIds}
                                    relatedCategoryCategoryIds={
                                        relatedCategoryCategoryIds
                                    }
                                    relatedCategoryBrandIds={
                                        relatedCategoryBrandIds
                                    }
                                    watchlistId={watchlistId}
                                    countryTextId={countryTextId}
                                    watchlistItems={watchlistItems}
                                    onRemoveItem={onRemoveItem}
                                    onAddItem={onAddItem}
                                    watchlistName={watchlistName}
                                />
                            ) : !watchlist ? (
                                <WatchlistNotFound />
                            ) : (
                                <EmptyWatchlist
                                    hasUnsupportedItemsOnly={
                                        hasUnsupportedItemsOnly
                                    }
                                    countryTextId={countryTextId}
                                    renderHits={renderHits}
                                />
                            )}
                        </SectionCard>
                    )}
                    {dataSource === activeDataSource.google && (
                        <SectionCard css="padding-top: 20px;">
                            {isLoadingWatchlistItems || isLoadingWatchlists ? (
                                <Loading />
                            ) : watchlist && filteredWatchlistItems.length ? (
                                <SearchWatchlist
                                    key={watchlistId}
                                    brandIds={brandIds}
                                    categoryIds={categoryIds}
                                    affinityCategoryIds={affinityCategoryIds}
                                    affinityBrandIds={affinityBrandIds}
                                    watchlistId={watchlistId}
                                    country={country}
                                    countryId={countryId}
                                    countryTextId={countryTextId}
                                    mixpanelMeta={mixpanelMeta}
                                    onRemoveItem={onRemoveItem}
                                    onRowClick={onRowClick}
                                    renderHits={renderHits}
                                    watchlistName={watchlistName}
                                />
                            ) : !watchlist ? (
                                <WatchlistNotFound />
                            ) : (
                                <EmptyWatchlist
                                    hasUnsupportedItemsOnly={
                                        hasUnsupportedItemsOnly
                                    }
                                    countryTextId={countryTextId}
                                    renderHits={renderHits}
                                />
                            )}
                        </SectionCard>
                    )}
                    {dataSource === activeDataSource.tiktok && (
                        <SectionCard css="padding-top: 20px;">
                            {isLoadingWatchlistItems || isLoadingWatchlists ? (
                                <Loading />
                            ) : watchlist && filteredWatchlistItems.length ? (
                                <TiktokWatchlist
                                    categoryIds={categoryIds}
                                    brandIds={brandIds}
                                    watchlistId={watchlistId}
                                    countryTextId={countryTextId}
                                    watchlistItems={watchlistItems}
                                    onRemoveItem={onRemoveItem}
                                    onAddItem={onAddItem}
                                    watchlistName={watchlistName}
                                    hashtagCategoryIds={hashtagCategoryIds}
                                    hashtagBrandIds={hashtagBrandIds}
                                    relatedCategoryParentCategoryItems={
                                        relatedCategoryParentCategoryItems
                                    }
                                    relatedCategoryParentBrandItems={
                                        relatedCategoryParentBrandItems
                                    }
                                    categoryPairs={tiktokRelatedCategoryPairs}
                                    brandPairs={tiktokRelatedBrandPairs}
                                />
                            ) : !watchlist ? (
                                <WatchlistNotFound />
                            ) : (
                                <EmptyWatchlist
                                    hasUnsupportedItemsOnly={
                                        hasUnsupportedItemsOnly
                                    }
                                    countryTextId={countryTextId}
                                    renderHits={renderHits}
                                />
                            )}
                        </SectionCard>
                    )}
                    {dataSource === activeDataSource.instagram && (
                        <SectionCard css="padding-top: 20px;">
                            {isLoadingWatchlistItems || isLoadingWatchlists ? (
                                <Loading />
                            ) : watchlist && filteredWatchlistItems.length ? (
                                <InstagramWatchlist
                                    categoryIds={categoryIds}
                                    brandIds={brandIds}
                                    instagramHashtags={instagramHashtags}
                                    relatedCategoryParentCategoryItems={
                                        relatedCategoryParentCategoryItems
                                    }
                                    relatedCategoryParentBrandItems={
                                        relatedCategoryParentBrandItems
                                    }
                                    categoryPairs={tiktokRelatedCategoryPairs}
                                    brandPairs={tiktokRelatedBrandPairs}
                                    watchlistId={watchlistId}
                                    countryTextId={countryTextId}
                                    watchlistItems={watchlistItems}
                                    onRemoveItem={onRemoveItem}
                                    onAddItem={onAddItem}
                                    watchlistName={watchlistName}
                                />
                            ) : !watchlist ? (
                                <WatchlistNotFound />
                            ) : (
                                <EmptyWatchlist
                                    hasUnsupportedItemsOnly={
                                        hasUnsupportedItemsOnly
                                    }
                                    countryTextId={countryTextId}
                                    renderHits={renderHits}
                                />
                            )}
                        </SectionCard>
                    )}
                </Stack>
            </PagePadding>
            <Drawer open={!!details} onClose={() => setDetails(null)}>
                {details && (
                    <DrawerContent showAddToWatchlist={false} {...details} />
                )}
            </Drawer>
        </>
    );
}

export default WatchlistView;
